// Cookie consent for www.boldpiq.com: the same module every BoldPiq client site uses
// (boldpiq-site-template, codebase standard §3c), adapted to React. The banner is
// src/components/ui/ConsentBanner.tsx (vanilla-cookieconsent, MIT, bundled: no third-party CMP).
//
// Every tracker component asks `useConsent(category)` and loads nothing until it is true:
//   functional -> GHL live chat        analytics -> GA4, Microsoft Clarity
//   marketing  -> Meta Pixel           necessary -> the consent cookie, Turnstile, forms you open
// Every choice is logged to consent.boldpiq.com (consent-worker/ in this repo, D1, 12 months, no IP).
"use client"
import { useSyncExternalStore } from "react"
import * as CookieConsent from "vanilla-cookieconsent"

export type ConsentCategory = "necessary" | "functional" | "analytics" | "marketing"

/** Raise when the list of trackers changes: every visitor is asked again. */
export const CONSENT_VERSION = 1
export const CONSENT_LOG_URL = "https://consent.boldpiq.com/api/consent"

// vanilla-cookieconsent fires these on window after a choice is loaded or changed.
const EVENTS = ["cc:onConsent", "cc:onChange"] as const

function subscribe(onChange: () => void) {
  EVENTS.forEach((e) => window.addEventListener(e, onChange))
  return () => EVENTS.forEach((e) => window.removeEventListener(e, onChange))
}

export function hasConsent(category: ConsentCategory): boolean {
  try {
    return CookieConsent.validConsent() && CookieConsent.acceptedCategory(category)
  } catch {
    return false
  }
}

/** True once the visitor has accepted `category`; re-renders when they change their mind. Server: false. */
export function useConsent(category: ConsentCategory): boolean {
  return useSyncExternalStore(subscribe, () => hasConsent(category), () => false)
}

/** Google Consent Mode v2 signals from the current choice. */
export function googleConsentSignals() {
  const ads = hasConsent("marketing") ? "granted" : "denied"
  return {
    analytics_storage: hasConsent("analytics") ? "granted" : "denied",
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  }
}
